/**
 * Every match for what was typed in the Hondo bar: cattle, records, jobs and the tools that do the
 * job ("register calves"), grouped and in one place. Results only include what this person may see;
 * the server enforces it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import type { SearchHit } from "../../../contract";
import { qs } from "../../lib/api";
import { t, useLanguage } from "../../lib/i18n";
import { useApi } from "../../lib/query";
import { useCan } from "../../lib/session";
import { Button, Empty, ErrorNote, Icon, List, Loading, PageHeader, Row, Section, TextInput, cx, useFromHere } from "../../ui/kit";
import { hitIcon, useOpenComposer } from "../Composer";
import { Page } from "../Layout";
import { allTools, matchTools, type Tool } from "../tools";

/** Marks an English catalog key (collected by the i18n check); translated with t() where shown. */
const tr = (english: string) => english;

/** Result groups, in the order they show. */
export const SEARCH_GROUPS: Array<{ id: string; label: string; kinds: Array<SearchHit["kind"] | "tool"> }> = [
  { id: "cattle", label: tr("Cattle"), kinds: ["animal"] },
  { id: "records", label: tr("Records"), kinds: ["event", "task", "capture"] },
  { id: "jobs", label: tr("Jobs"), kinds: ["job"] },
  { id: "tools", label: tr("Tools"), kinds: ["tool"] },
  { id: "places", label: tr("Places and groups"), kinds: ["place", "group"] },
  { id: "people", label: tr("People and businesses"), kinds: ["contact"] },
  { id: "things", label: tr("Supplies and equipment"), kinds: ["stock_item", "stock_lot", "asset"] },
  { id: "money", label: tr("Finances"), kinds: ["fin_doc"] },
];

interface Result { key: string; kind: SearchHit["kind"] | "tool"; icon: string; title: string; meta?: string; to: string }

/** Turn server hits and matching tools into grouped results. Pure, for tests. */
export function groupResults(hits: SearchHit[], tools: Tool[]): Array<{ id: string; label: string; results: Result[] }> {
  const all: Result[] = [
    ...hits.map((h) => ({ key: `${h.kind}:${h.id}`, kind: h.kind, icon: hitIcon(h.kind), title: h.title, meta: h.subtitle, to: h.link })),
    ...tools.map((w) => ({ key: `tool:${w.id}`, kind: "tool" as const, icon: w.icon, title: t(w.label), meta: t(w.description), to: w.to })),
  ];
  return SEARCH_GROUPS.map((g) => ({ id: g.id, label: g.label, results: all.filter((r) => g.kinds.includes(r.kind)) })).filter((g) => g.results.length > 0);
}

const SHOWN = 6;

export function SearchPage() {
  useLanguage();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const can = useCan();
  const openComposer = useOpenComposer();
  const q = params.get("q") ?? "";
  const [text, setText] = useState(q);
  const [only, setOnly] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const input = useRef<HTMLInputElement>(null);
  const from = useFromHere(q ? t("Results for “{{text}}”", { text: q }) : t("Search"));
  useEffect(() => setText(q), [q]);
  useEffect(() => { if (!q) input.current?.focus(); }, [q]);
  // Typing updates the address so Back returns to these results.
  useEffect(() => {
    const h = setTimeout(() => { if (text.trim() !== q) setParams(text.trim() ? { q: text.trim() } : {}, { replace: true }); }, 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  useEffect(() => { setOnly(null); setExpanded(new Set()); }, [q]);

  const hits = useApi<{ items: SearchHit[] }>(q ? `/search${qs({ q, limit: 60 })}` : null, { placeholderData: (prev) => prev });
  const tools = useMemo(() => (q ? matchTools(q, allTools(can)) : []), [q, can]);
  const groups = useMemo(() => groupResults(hits.data?.items ?? [], tools), [hits.data, tools]);
  const total = groups.reduce((n, g) => n + g.results.length, 0);
  const shown = only ? groups.filter((g) => g.id === only) : groups;

  return (
    <Page>
      <PageHeader title={t("Search")} subtitle={q ? (hits.isFetching && !hits.data ? t("Looking…") : t(total === 1 ? "{{count}} result for “{{text}}”" : "{{count}} results for “{{text}}”", { count: total, text: q })) : t("Find cattle, records, jobs, places, people and tools.")} />
      <form role="search" className="search-form" onSubmit={(e) => { e.preventDefault(); setParams(text.trim() ? { q: text.trim() } : {}, { replace: true }); }}>
        <div className="search-input search-input-lg">
          <Icon name="search" size={20} />
          <TextInput ref={input} type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("Tag, name, place, job or what you want to do")} aria-label={t("Search the ranch")} enterKeyHint="search" />
        </div>
      </form>
      {q && groups.length > 1 && (
        <div className="filter-chips" role="group" aria-label={t("Show")}>
          <button type="button" className={cx("choice", !only && "choice-on")} aria-pressed={!only} onClick={() => setOnly(null)}>{t("Everything")}</button>
          {groups.map((g) => <button key={g.id} type="button" className={cx("choice", only === g.id && "choice-on")} aria-pressed={only === g.id} onClick={() => setOnly(only === g.id ? null : g.id)}>{t(g.label)} <span className="count">{g.results.length}</span></button>)}
        </div>
      )}
      {!q ? null : hits.isLoading && !tools.length ? <Loading /> : hits.error && !hits.data && !tools.length ? <ErrorNote error={hits.error} retry={() => hits.refetch()} /> : total === 0 ? (
        <Empty icon="notFound" title={t("Nothing matches “{{text}}”", { text: q })}
          body={t("Try a tag without letters, part of a name, or ask Hondo in your own words.")}
          action={<Button variant="primary" icon="sparkle" onClick={() => openComposer({ draft: q })}>{t("Ask Hondo")}</Button>} />
      ) : (
        <div className="search-results">
          {shown.map((g) => {
            const open = only === g.id || expanded.has(g.id);
            const list = open ? g.results : g.results.slice(0, SHOWN);
            return (
              <Section key={g.id} title={t(g.label)} count={g.results.length}>
                <List>{list.map((r) => <Row key={r.key} icon={r.icon} title={r.title} meta={r.meta} to={r.to} state={from} />)}</List>
                {!open && g.results.length > SHOWN && <Button variant="ghost" onClick={() => setExpanded((s) => new Set(s).add(g.id))}>{t("Show all {{count}}", { count: g.results.length })}</Button>}
              </Section>
            );
          })}
          <div className="search-ask">
            <p className="muted">{t("Not what you meant?")}</p>
            <Button icon="sparkle" onClick={() => openComposer({ draft: q })}>{t("Ask Hondo: “{{text}}”", { text: q.slice(0, 60) })}</Button>
            <Button variant="ghost" icon="back" onClick={() => navigate(-1)}>{t("Back")}</Button>
          </div>
        </div>
      )}
    </Page>
  );
}
